import { useEffect, useState } from "react";
import {
    Collapse,
    Tag,
    Rate,
    Typography,
    Empty,
    Spin,
} from "antd";
import {
    RetweetOutlined,
    UserOutlined,
    TeamOutlined,
    HistoryOutlined,
    SolutionOutlined,
    StarOutlined,
} from "@ant-design/icons";
import { PageHeader } from "../../../../components/page-header/page-header";
import { getUserAssessmentHistory } from "../../../../utils/api.constant";
import useFetch from "../../../../hooks/useFetch";
import { eResultCode } from "../../../../utils/enum";
import dayjs from "dayjs";
import "./userAssessmentHistory.css";

const { Panel } = Collapse;
const { Text, Title } = Typography;

interface SelfAssessmentKPI {
    kpiId: number;
    kpiName: string;
    description: string;
    value: string;
    comment: string;
    category: string;
}

interface ReviewerKPI {
    kpiId: number;
    kpiName: string;
    description: string;
    manager_value: string | null;
    manager_comment: string | null;
    hr_value: string | null;
    hr_comment: string | null;
}

interface AssessmentHistoryItem {
    id: number;
    assessmentId: number;
    status: string;
    remarks: string;
    performedBy: number;
    createdOn: string;
}

interface AssessmentData {
    id: number;
    name: string;
    createdOn: string;
    grade: string;
    rating: string;
    popupData: {
        selfAssessment: SelfAssessmentKPI[];
        reviewer: ReviewerKPI[];
        correctivePlan: string | null;
        history: AssessmentHistoryItem[];
    };
}

export default function UserAssessmentHistoryList() {
    const { post } = useFetch();
    const [isLoading, setIsLoading] = useState(false);
    const [assessmentData, setAssessmentData] = useState<AssessmentData[]>([]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const requestPayload = {
                data: {
                    currentPage: 1,
                    pageSize: -1,
                },
            };

            const response = await post(getUserAssessmentHistory, requestPayload);
            if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
                setAssessmentData(response.data || []);
            }
        } catch (error) {
            console.error("Error fetching assessment history:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getGradeClass = (grade: string) => {
        if (grade.startsWith("A")) return "grade-A";
        if (grade.startsWith("B")) return "grade-B";
        if (grade.startsWith("C")) return "grade-C";
        if (grade.startsWith("D")) return "grade-D";
        return "";
    };

    const renderHeader = (item: AssessmentData) => (
        <div className="assessment-header-content">
            <div className="assessment-title-group">
                <Text className="assessment-name">{item.name}</Text>
                <Text className="assessment-date">Created on: {item.createdOn}</Text>
            </div>
            <div className="assessment-stats">
                <div className="stat-item">
                    <span className="stat-label">Rating</span>
                    <span className="stat-value">{item.rating}</span>
                </div>
                <div className="stat-item">
                    <span className="stat-label">Grade</span>
                    <span className={`grade-badge ${getGradeClass(item.grade)}`}>{item.grade}</span>
                </div>
            </div>
        </div>
    );

    const renderCategorySection = (item: AssessmentData, category: string) => {
        const filteredSelf = item.popupData.selfAssessment.filter(k => k.category === category);
        if (filteredSelf.length === 0) return null;

        return (
            <div className="category-container" key={category}>
                <div className="category-header">
                    <Tag color={category === "TECHNICAL" ? "geekblue" : "purple"} style={{ fontSize: '12px', fontWeight: 600, padding: '4px 12px' }}>
                        {category}
                    </Tag>
                </div>

                <div className="kpi-card-list">
                    {filteredSelf.map((selfKpi) => {
                        const reviewerKpi = item.popupData.reviewer.find(r => r.kpiId === selfKpi.kpiId);
                        return (
                            <div key={selfKpi.kpiId} className="shared-kpi-card">
                                <div className="shared-kpi-name">{selfKpi.kpiName}</div>
                                <div className="shared-kpi-content">
                                    {/* Left: Self */}
                                    <div className="shared-col">
                                        <div className="kpi-rating-row">
                                            <Rate disabled defaultValue={parseFloat(selfKpi.value)} allowHalf />
                                            <span className="kpi-rating-badge">{selfKpi.value}</span>
                                        </div>
                                        <div className="kpi-comment-label">
                                            {selfKpi.comment || "No comment provided."}
                                        </div>
                                    </div>

                                    {/* Right: Reviewer */}
                                    <div className="shared-col reviewer-side">
                                        {category === "TECHNICAL" ? (
                                            reviewerKpi?.manager_value ? (
                                                <>
                                                    <div className="kpi-rating-row">
                                                        <Rate disabled defaultValue={parseFloat(reviewerKpi.manager_value)} allowHalf />
                                                        <span className="kpi-rating-badge">{reviewerKpi.manager_value}</span>
                                                    </div>
                                                    <div className="kpi-comment-label">
                                                        {reviewerKpi.manager_comment}
                                                    </div>
                                                </>
                                            ) : <Text type="secondary" italic>Not reviewed by Manager</Text>
                                        ) : (
                                            reviewerKpi?.hr_value ? (
                                                <>
                                                    <div className="kpi-rating-row">
                                                        <Rate disabled defaultValue={parseFloat(reviewerKpi.hr_value)} allowHalf />
                                                        <span className="kpi-rating-badge">{reviewerKpi.hr_value}</span>
                                                    </div>
                                                    <div className="kpi-comment-label">
                                                        {reviewerKpi.hr_comment}
                                                    </div>
                                                </>
                                            ) : <Text type="secondary" italic>Not reviewed by HR</Text>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="pageBody">
            <PageHeader
                icon={<RetweetOutlined />}
                title="User Assessment History"
                size="large"
            />

            <div className="content-wrap" style={{ padding: '0 24px' }}>
                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <Spin size="large" />
                    </div>
                ) : assessmentData.length > 0 ? (
                    <Collapse
                        accordion
                        className="assessment-accordion"
                        expandIconPosition="right"
                    >
                        {assessmentData.map((item) => (
                            <Panel header={renderHeader(item)} key={item.id}>
                                <div className="assessment-details">

                                    {/* Master Headers */}
                                    <div className="assessment-master-headers">
                                        <div className="master-header-col">
                                            <Title level={5} className="section-title"><UserOutlined /> Self Assessment</Title>
                                        </div>
                                        <div className="master-header-col">
                                            <Title level={5} className="section-title"><TeamOutlined /> Reviewer Feedback</Title>
                                        </div>
                                    </div>

                                    {renderCategorySection(item, "TECHNICAL")}
                                    {renderCategorySection(item, "BEHAVIORAL")}

                                    {/* Corrective Plan */}
                                    {item.popupData.correctivePlan && (
                                        <div className="details-section">
                                            <Title level={5} className="section-title">
                                                <SolutionOutlined /> Corrective Plan
                                            </Title>
                                            <div className="corrective-plan-box">
                                                {item.popupData.correctivePlan}
                                            </div>
                                        </div>
                                    )}

                                    {/* History Timeline */}
                                    <div className="details-section">
                                        <Title level={5} className="section-title">
                                            <HistoryOutlined /> Status History
                                        </Title>
                                        <div className="history-timeline">
                                            {item.popupData.history.map((hist) => (
                                                <div key={hist.id} className="history-item">
                                                    <div className="history-dot"></div>
                                                    <div className="history-content">
                                                        <div>
                                                            <Tag color="blue" className="history-status">{hist.status.replace(/_/g, ' ')}</Tag>
                                                            <span className="history-time">{dayjs(hist.createdOn).format("DD MMM YYYY, hh:mm A")}</span>
                                                        </div>
                                                        <div className="history-remarks">{hist.remarks}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </Panel>
                        ))}
                    </Collapse>
                ) : (
                    <div className="empty-state">
                        <Empty description="No assessment history found" />
                    </div>
                )}
            </div>
        </div>
    );
}
